import React, { useState } from "react";
import { Manager, Popper, PopperProps, Reference, StrictModifier } from "react-popper";
import classNames from "classnames";
// @ts-ignore
import { Tooltip } from "@hopper/tooltip";

type TooltipContent = JSX.Element | string;
type ArrayOrElement<T> = Array<T> | T;
type Theme = "light" | "dark";
type TooltipMarginSize = "md" | "sm" | "0";
type TriggerFunction = (
  e: React.MouseEvent<HTMLElement, MouseEvent> | React.FocusEvent<HTMLSpanElement, Element>,
) => void;

export type TooltipTriggerProps = {
  tooltip: TooltipContent;
  children: ArrayOrElement<JSX.Element>;
  theme?: Theme;
  tooltipMarginSize?: TooltipMarginSize;
  placement?: Readonly<PopperProps<StrictModifier>>["placement"];
  onMouseEnter?: TriggerFunction;
  onMouseLeave?: TriggerFunction;
  tooltipWidth?: string;
  className?: string;
};

const noop = () => {};

const TooltipTrigger = ({
  tooltip,
  children,
  theme = "dark",
  tooltipMarginSize = "md",
  placement = "top",
  onMouseEnter = noop,
  onMouseLeave = noop,
  className,
  tooltipWidth,
}: TooltipTriggerProps): JSX.Element => {
  const marginSizes = { sm: "p-sm", md: "p-md", 0: "p-0" };
  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const tooltipClassNames = classNames(["z-50", marginSizes[tooltipMarginSize]], className);
  const tooltipStyle = tooltipWidth ? { width: tooltipWidth } : {};

  return (
    <Manager>
      <Reference>
        {({ ref }) => (
          <span
            className="inline-block"
            tabIndex={0}
            ref={ref}
            onMouseEnter={event => {
              onMouseEnter(event);
              setShouldShowTooltip(true);
            }}
            onMouseLeave={event => {
              setShouldShowTooltip(false);
              onMouseLeave(event);
            }}
            onFocus={() => setShouldShowTooltip(true)}
            onBlur={() => setShouldShowTooltip(false)}
            onKeyDown={event => {
              if (event.key === "Escape") setShouldShowTooltip(false);
            }}
          >
            {children}
          </span>
        )}
      </Reference>
      <Popper placement={placement}>
        {({ ref, style }) =>
          shouldShowTooltip && (
            <div ref={ref} style={style} className={tooltipClassNames}>
              <Tooltip theme={theme}>
                <div style={tooltipStyle} data-testid="tooltip">
                  {tooltip}
                </div>
              </Tooltip>
            </div>
          )
        }
      </Popper>
    </Manager>
  );
};

export default TooltipTrigger;
